import {useCallback, useRef, useState} from 'react';
import {SaveState} from '../shared/saveState';

export const PromptTarget = {
	System: 'system',
	User: 'user',
	AgentMd: 'agentMd',
} as const;
export type PromptTarget = (typeof PromptTarget)[keyof typeof PromptTarget];

export interface PromptInputs {
	systemPrompt: string;
	userPromptTemplate: string;
	agentMd: string;
}

export interface UseNovaPromptStateOptions {
	initialValues?: Partial<PromptInputs>;
	onSave?: (values: PromptInputs) => Promise<void>;
}

export interface NovaPromptState {
	values: PromptInputs;
	activeTarget: PromptTarget;
	// Whichever of the System prompt or User prompt template was focused most recently, regardless of
	// how many times agent.md has been the active target since. Starts at User, since that is the
	// input an author who has never focused either editor should land in.
	lastPromptTarget: PromptTarget;
	isDirty: boolean;
	saveState: SaveState;
	setActiveTarget: (target: PromptTarget, cursorOffset?: number) => void;
	setValue: (target: PromptTarget, value: string) => void;
	insertTokenAtCursor: (token: string) => void;
	load: (inputs: PromptInputs) => void;
	save: () => Promise<void>;
}

const EMPTY_INPUTS: PromptInputs = {
	systemPrompt: '',
	userPromptTemplate: '',
	agentMd: '',
};

const FIELD_BY_TARGET: Record<PromptTarget, keyof PromptInputs> = {
	[PromptTarget.System]: 'systemPrompt',
	[PromptTarget.User]: 'userPromptTemplate',
	[PromptTarget.AgentMd]: 'agentMd',
};

// A cursor beyond the field length resolves to the end of the input — the
// natural target before the author has focused/placed the caret anywhere.
const CURSOR_AT_END = Number.MAX_SAFE_INTEGER;

function sameInputs(first: PromptInputs, second: PromptInputs): boolean {
	return (
		first.systemPrompt === second.systemPrompt &&
		first.userPromptTemplate === second.userPromptTemplate &&
		first.agentMd === second.agentMd
	);
}

function deriveSaveState(isSaving: boolean, saveFailed: boolean, isDirty: boolean): SaveState {
	if (isSaving) {
		return SaveState.Saving;
	}
	if (saveFailed) {
		return SaveState.Error;
	}
	if (isDirty) {
		return SaveState.Unsaved;
	}
	return SaveState.Saved;
}

export function useNovaPromptState(options: UseNovaPromptStateOptions = {}): NovaPromptState {
	const {onSave} = options;
	const initialValues: PromptInputs = {...EMPTY_INPUTS, ...options.initialValues};

	const [values, setValues] = useState<PromptInputs>(initialValues);
	const [baseline, setBaseline] = useState<PromptInputs>(initialValues);
	const [activeTarget, setActiveTargetState] = useState<PromptTarget>(PromptTarget.User);
	const [cursor, setCursor] = useState<number>(CURSOR_AT_END);
	const [isSaving, setIsSaving] = useState<boolean>(false);
	const [saveFailed, setSaveFailed] = useState<boolean>(false);

	// Tracked outside state: nothing needs to re-render when this changes, only the Prompt tab's own
	// mount effect ever reads it, and putting it in state would give setActiveTarget a dependency it
	// would otherwise not need, defeating its stable identity.
	const lastPromptTargetRef = useRef<PromptTarget>(PromptTarget.User);

	const isDirty = !sameInputs(values, baseline);
	const saveState = deriveSaveState(isSaving, saveFailed, isDirty);

	const setActiveTarget = useCallback((target: PromptTarget, cursorOffset?: number) => {
		if (target === PromptTarget.System || target === PromptTarget.User) {
			lastPromptTargetRef.current = target;
		}
		setActiveTargetState(target);
		setCursor(cursorOffset ?? CURSOR_AT_END);
	}, []);

	const setValue = useCallback((target: PromptTarget, value: string) => {
		const field = FIELD_BY_TARGET[target];
		setValues(previous => ({...previous, [field]: value}));
		setSaveFailed(false);
	}, []);

	const load = useCallback((inputs: PromptInputs) => {
		setValues(inputs);
		setBaseline(inputs);
		setSaveFailed(false);
	}, []);

	const save = useCallback(async () => {
		if (!onSave) {
			throw new Error('useNovaPromptState: no onSave provided');
		}
		setIsSaving(true);
		setSaveFailed(false);
		try {
			await onSave(values);
			setBaseline(values);
		} catch (error) {
			setSaveFailed(true);
			throw error;
		} finally {
			setIsSaving(false);
		}
	}, [onSave, values]);

	// Insertion cannot refuse: every authored input can reference every variable, so there is no
	// question to ask about the token before splicing it in.
	const insertTokenAtCursor = useCallback(
		(token: string): void => {
			const field = FIELD_BY_TARGET[activeTarget];
			const current = values[field];
			const position = Math.max(0, Math.min(cursor, current.length));
			setValues(previous => ({
				...previous,
				[field]: previous[field].slice(0, position) + token + previous[field].slice(position),
			}));
			setCursor(position + token.length);
			setSaveFailed(false);
		},
		[activeTarget, cursor, values]
	);

	return {
		values,
		activeTarget,
		lastPromptTarget: lastPromptTargetRef.current,
		isDirty,
		saveState,
		setActiveTarget,
		setValue,
		insertTokenAtCursor,
		load,
		save,
	};
}
